import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { GenericError } from '@skynet/data-ws-core';
import { registerErrorMessage, WsPrefixes, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { skyRouter } from 'src/routes/sky-router';
import { ApplicationDomain } from 'src/routes/types';

import { describeWsFailure } from 'src/store/utils';
import { actionOnMessage, wsMmSend } from '@skynet/data-ws-mm';

import { createAppAsyncThunk } from 'src/store/utils/thunks';
import { selectAuthDomain, selectUserTokenES, selectUserTokenMM } from 'src/store/auth/selectors';
import { ensureAccessToken, getTokenPayload, selectAccessToken } from '@skynet/data-auth-api';
import type { AuthState, JsonAuthSuccess, LoginDomainData, LoginDomainPayload } from './types';
import { AuthCommands } from './types';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import { requiresLoginCommand } from 'src/utils/routing/validation';
import { actionEsOnMessage, wsEsSend } from '@skynet/data-ws-es';
import { signupApiEndpoints } from '@skynet/data-signup';
import { isApplicationDomain } from 'src/hooks/useSection';
import { signOut } from 'src/store/signup/slice';

const allowedCommands = Object.values(AuthCommands);

registerErrorMessage(AuthCommands.Authorization, {
  title: 'Authorization Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(AuthCommands['2FA'], {
  title: '2FA Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(AuthCommands.Login, {
  title: 'Login Error',
  getDescription: describeWsFailure,
});

const initialState: AuthState = {
  error: null,
  redirectUrl: '/',
  res2FA: null,
  resAuth: null, // deprecated
  isAuthedMM: false,
  isAuthedES: false,
  tokenMM: undefined,
  tokenES: undefined,

  // token: null,
  // name: '',
  // userId,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setRedirectUrl(state, action: PayloadAction<{ url: string }>) {
      state.redirectUrl = action.payload.url;
    },
    setAuthDomain(state, action: PayloadAction<{ domain: string }>) {
      state.authDomain = action.payload.domain;
    },
    processAuthOk(state, action: PayloadAction<{ json: JsonAuthSuccess; wsType?: WsPrefixes }>) {
      const { token, Name = '', accessLevel, lastAccess /* , userID */ } = action.payload.json;
      const { wsType } = action.payload;

      state.error = null;
      state.res2FA = 'success';

      state.isAuthedMM = wsType === WsPrefixes.mmPrefix;
      state.isAuthedES = wsType === WsPrefixes.esPrefix;

      if (wsType === WsPrefixes.mmPrefix) {
        state.tokenMM = token;
        // state.tokenES = undefined; // clear ES token if MM is used
      }

      if (wsType === WsPrefixes.esPrefix) {
        state.tokenES = token;
        // state.tokenMM = undefined; // clear MM token if ES is used
      }

      state.name = Name;
      // state.userId = userID || 0;
      state.lastAccess = lastAccess;
      state.accessLevel = accessLevel;
    },
    processNotConnected(state) {
      state.notConnected = true;
      state.authDomain = undefined;
    },
    processAuthRequired(state) {
      state.tokenMM = undefined;
      state.tokenES = undefined;
      state.isAuthedMM = false;
      state.isAuthedES = false;
    },
    // TODO: do we need it? it seems deprecated
    processOkAuthorization(state) {
      state.error = null;
      state.resAuth = 'success';
    },
    processError(
      state,
      action: PayloadAction<{ error: GenericError; key?: 'resAuth' | 'res2FA' }>,
    ) {
      state.error = action.payload.error;
      if (action.payload.key) {
        state[action.payload.key] = 'error';
      }
    },
  },
  extraReducers: (builder) => {
    // logout
    // TODO: handle logout for ES as well
    builder.addCase(signOut.fulfilled, (state, action) => {
      // Add user to the state array
      console.debug('auth/signOut', action.payload);
      return initialState;
    });
  },
});

function getLoginExtra(domain?: string) {
  switch (domain) {
    case ApplicationDomain.Loans:
    case ApplicationDomain.Profile:
    case ApplicationDomain.Admin:
      return { loan: 1 };
    case ApplicationDomain.Retainers:
      return { retainer: 1 };
    case ApplicationDomain.Trading:
      return { trading: 1 };
    case ApplicationDomain.DEX:
    case undefined:
    default:
      return null;
  }
}

export const loginDomain = createAppAsyncThunk(
  'auth/login',
  async ({ domain, forceLogin = false, slug = '' }: LoginDomainData, thunkApi) => {
    const state = thunkApi.getState();
    const token =
      domain === ApplicationDomain.Trading ? selectUserTokenES(state) : selectUserTokenMM(state);
    const accessToken = selectAccessToken(state);
    const authDomain = selectAuthDomain(state);

    if (authDomain === domain && !forceLogin) {
      return undefined;
    }

    const extra = getLoginExtra(domain);

    if (!accessToken || !extra) {
      return undefined;
    }

    const { isMfaAuthenticated, isTokenExpired } = getTokenPayload(accessToken);
    const isAccessTokenValid = accessToken && isMfaAuthenticated && !isTokenExpired; // && !token;

    if (!isAccessTokenValid) {
      await thunkApi.dispatch(signupApiEndpoints.refreshToken.initiate());
    }

    // as per backend request we should send accessToken always now
    const payload: LoginDomainPayload = {
      ...(token && { token }),
      accessToken,
      ...extra,
      ...(slug && { slug }),
    };

    thunkApi.dispatch(authSlice.actions.setAuthDomain({ domain }));

    if (domain === ApplicationDomain.Trading) {
      return thunkApi.dispatch(
        wsEsSend({
          command: AuthCommands.Login,
          ...payload,
        }),
      );
    }

    thunkApi.dispatch(
      wsMmSend({
        command: AuthCommands.Login,
        ...payload,
      }),
    );
    return undefined;
  },
);

export const authActions = authSlice.actions;

export default authSlice.reducer;

// we use listener here to process messages from both ES and MM WS connections
// as they have the same login but different commands
startAppListening({
  matcher: isAnyOf(actionOnMessage, actionEsOnMessage),
  effect: async (action: PayloadAction<{ message: string }>, listenerApi) => {
    const { json, skipProcessing, error, errorCommand } = parseResponse(
      action.payload,
      allowedCommands,
    );

    if (skipProcessing || !json) return;

    const { command, okCommand } = json;

    if (error) {
      let errorKey: 'resAuth' | 'res2FA' | undefined;

      // TODO: do we need it? these error commands seem deprecated
      if (errorCommand === AuthCommands.Authorization) {
        errorKey = 'resAuth';
      } else if (errorCommand === AuthCommands['2FA']) {
        errorKey = 'res2FA';
      }

      listenerApi.dispatch(authActions.processError({ key: errorKey, error }));
      return;
    }

    if (isOkCommand(command)) {
      switch (okCommand) {
        case AuthCommands.Authorization:
          listenerApi.dispatch(authActions.processOkAuthorization());
          return;

        default:
          break;
      }
    }

    if (command === AuthCommands.authOK.toString()) {
      let wsType: WsPrefixes | undefined = undefined;
      if (action.type.startsWith(WsPrefixes.mmPrefix)) {
        wsType = WsPrefixes.mmPrefix;
      } else if (action.type.startsWith(WsPrefixes.esPrefix)) {
        wsType = WsPrefixes.esPrefix;
      }

      const payload = { json, wsType } as { json: JsonAuthSuccess; wsType?: WsPrefixes };

      listenerApi.dispatch(authActions.processAuthOk(payload));
      return;
    }

    if (command === AuthCommands.NotConnected.toString()) {
      listenerApi.dispatch(authActions.processNotConnected());
      return;
    }

    if (command === AuthCommands.authRequired.toString()) {
      listenerApi.dispatch(authActions.processAuthRequired());

      const state = listenerApi.getState();
      let domain: ApplicationDomain = ApplicationDomain.Loans;
      if (isApplicationDomain(state.auth.authDomain)) {
        domain = state.auth.authDomain;
      } else {
        const routerDomain = skyRouter.getDomain();
        if (isApplicationDomain(routerDomain)) {
          domain = routerDomain;
        }
      }

      const pathname = skyRouter.getPathname();

      if (requiresLoginCommand(pathname)) {
        await listenerApi.dispatch(loginDomain({ domain, forceLogin: true }));
      }
      return;
    }
  },
});

// TODO: do we really need this?
startAppListening({
  matcher: isAnyOf(signupApiEndpoints.authenticateMfa.matchFulfilled),
  effect: async (_, api) => {
    const accessToken = selectAccessToken(api.getState());
    await ensureAccessToken(api, accessToken);
  },
});
